import { useId } from 'react'
import type { ReactNode } from 'react'

/**
 * List pagination: summary, first/prev/next/last, numbered pages with gaps,
 * and a page-size selector.
 *
 * Rendered above AND below long tables — with 100 rows on screen, a pager only
 * at the top means scrolling back up to move on.
 *
 * `total` is the server-side total across all pages, never the number of rows
 * currently loaded; the two differ and conflating them under-reports badly.
 */

export interface PaginationProps {
  /** 1-based. */
  page: number
  pageSize: number
  /** Total items across all pages, from the server. */
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  /** Disables the controls while a fetch is in flight. */
  busy?: boolean
  /** Distinguishes the top and bottom instances for screen readers. */
  label?: string
  /** Noun for the summary line, e.g. "sources". */
  unit?: string
  /** Extra content rendered at the start, e.g. filter chips. */
  children?: ReactNode
}

const DEFAULT_SIZES = [50, 100, 200, 500]

/**
 * Page numbers to render, with 'gap' where a run is elided.
 * Pure and exported so the windowing is unit-tested rather than eyeballed.
 */
export function pageItems(current: number, last: number, maxNumbers = 7): Array<number | 'gap'> {
  if (last <= 1) return [1]
  if (last <= maxNumbers) return Array.from({ length: last }, (_, i) => i + 1)

  // Slots left for the middle window once first, last and two gaps are placed.
  const windowSize = Math.max(1, maxNumbers - 4)
  let start = Math.max(2, current - Math.floor(windowSize / 2))
  let end = start + windowSize - 1
  if (end > last - 1) {
    end = last - 1
    start = Math.max(2, end - windowSize + 1)
  }

  const items: Array<number | 'gap'> = [1]
  if (start > 2) items.push('gap')
  for (let i = start; i <= end; i++) items.push(i)
  if (end < last - 1) items.push('gap')
  items.push(last)
  return items
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_SIZES,
  busy = false,
  label = 'Pagination',
  unit = 'items',
  children,
}: PaginationProps) {
  // Unique per instance: the top and bottom pagers both render this control.
  const sizeId = useId()
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(Math.max(1, page), lastPage)
  const firstRow = total === 0 ? 0 : (current - 1) * pageSize + 1
  const lastRow = Math.min(current * pageSize, total)
  const go = (n: number) => onPageChange(Math.min(lastPage, Math.max(1, n)))

  return (
    <nav className="pager" aria-label={label}>
      {children}

      <span className="pager-summary">
        {total === 0 ? `no ${unit}` : `${firstRow}–${lastRow} of ${total} ${unit}`}
      </span>

      <span className="pager-controls">
        <button
          type="button" className="pager-btn" onClick={() => go(1)}
          disabled={busy || current <= 1} aria-label="First page"
        >
          «
        </button>
        <button
          type="button" className="pager-btn" onClick={() => go(current - 1)}
          disabled={busy || current <= 1} aria-label="Previous page"
        >
          ‹ prev
        </button>

        {pageItems(current, lastPage).map((item, i) =>
          item === 'gap' ? (
            <span key={`gap-${i}`} className="pager-gap" aria-hidden="true">…</span>
          ) : (
            <button
              key={item}
              type="button"
              className="pager-btn pager-num"
              onClick={() => go(item)}
              disabled={busy}
              aria-current={item === current ? 'page' : undefined}
              aria-label={`Page ${item}${item === current ? ', current page' : ''}`}
            >
              {item}
            </button>
          ),
        )}

        <button
          type="button" className="pager-btn" onClick={() => go(current + 1)}
          disabled={busy || current >= lastPage} aria-label="Next page"
        >
          next ›
        </button>
        <button
          type="button" className="pager-btn" onClick={() => go(lastPage)}
          disabled={busy || current >= lastPage} aria-label="Last page"
        >
          »
        </button>
      </span>

      {onPageSizeChange ? (
        <label className="pager-size" htmlFor={sizeId}>
          <span>per page</span>
          <select
            id={sizeId}
            value={pageSize}
            disabled={busy}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      ) : null}
    </nav>
  )
}
